import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { Operations } from "../../../../../services/ItemServices";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildResourceOperationQueryParams,
  buildWizardValidationError,
  copyTextToClipboard,
  getErrorSummary,
  getId,
  getOperationItems,
  getOperationResult,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const SERVER_NAME = "convoke-client-mcp";
const PROTOCOL_VERSION = "2025-03-26";
const ROUTE_TEMPLATE = "/mcp/{clientShortName}";
const READINESS_STATE_FIELD = "__clientMcpToolsReadinessState";
const TEST_CURRENT_USER_CONNECTION_OPERATION = Operations.ResourceConfigurationItem?.TestClientMcpServerCurrentUserConnection
  || "TestClientMcpServerCurrentUserConnection";
const PERMISSION_DENIED_EXECUTE_CODE = "A9";
const CURRENT_USER_ACCESS_WARNING_CODE = "V5010";
const SECRET_KEY_PATTERN = /(password|secret|token|credential|connectionstring|privatekey|securecustomfields|authorization|auth)/i;

const getText = (...values) =>
  values
    .map((value) => String(value ?? "").trim())
    .find(Boolean) || "";

const normalizeRoutePart = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, "");

const getHostedApiBaseUri = () => {
  if (typeof window === "undefined") return "";
  const config = window.configData || window.config || {};
  const apiUris = config.API_URIS || config.apiUris || {};
  const convokeUri = getText(
    apiUris.CONVOKE,
    apiUris.Convoke,
    apiUris.convoke,
    config.CONVOKE_API_URI,
    config.convokeApiUri
  );
  return convokeUri.replace(/\/api\/Convoke\/?$/i, "").replace(/\/+$/, "");
};

const readCustomField = (item, ...keys) => {
  const customFields = readValue(item, "CustomFields", "customFields") || {};
  return getText(...keys.map((key) => customFields?.[key] ?? customFields?.[`${key.charAt(0).toLowerCase()}${key.slice(1)}`]));
};

const isTruthy = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const resolveClientShortName = ({ coreContext, wizard, values }) => normalizeRoutePart(getText(
  readValue(coreContext, "ClientShortCode", "clientShortCode"),
  readValue(wizard, "ClientShortCode", "clientShortCode"),
  readCustomField(readValue(wizard, "ResourceConfiguration", "resourceConfiguration"), "McpToolsClientShortName"),
  readValue(values, "McpToolsClientShortName", "clientShortName"),
  readValue(coreContext?.Profile, "ClientName", "clientName")
));

const buildToolsRoute = (clientShortName) =>
  clientShortName ? `/mcp/${clientShortName}` : ROUTE_TEMPLATE;

const buildAbsoluteUrl = (route) => {
  const baseUri = getHostedApiBaseUri();
  return baseUri ? `${baseUri}${route}` : route;
};

const redact = (value, key = "") => {
  if (SECRET_KEY_PATTERN.test(key)) return "[redacted]";
  if (Array.isArray(value)) return value.map((item) => redact(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [entryKey, redact(entryValue, entryKey)]));
  }
  return value;
};

const prettyJson = (value) => JSON.stringify(redact(value || {}), null, 2);

const formatFactValue = (value, emptyText = "Not set") => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : emptyText;
  const text = String(value ?? "").trim();
  return text || emptyText;
};

const formatCheckedTime = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString();
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const parseConnectionSnapshot = (response) => {
  const operationResult = getOperationResult(response);
  const value = readValue(operationResult, "Value", "value");
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getOperationMessageCode = (response) => {
  const operationResult = getOperationResult(response);
  return getText(
    readValue(readValue(operationResult, "Message", "message"), "Code", "code"),
    readValue(readValue(response, "Message", "message"), "Code", "code")
  ).toUpperCase();
};

const summarizeCheck = (check) => {
  if (!check) return "Not checked";
  if (check.ok) return check.detail || "Passed";
  return check.detail || "Failed";
};

const getStoredReadinessState = (values = {}) => {
  const state = readValue(values, READINESS_STATE_FIELD, "clientMcpToolsReadinessState") || {};
  if (state && typeof state === "object" && Object.keys(state).length) return state;
  const status = readValue(values, "ClientMcpToolsLastCheckStatus", "clientMcpToolsLastCheckStatus");
  if (!status) return {};
  return {
    hasRun: true,
    status,
    checkedAtUtc: readValue(values, "ClientMcpToolsLastCheckedUtc", "clientMcpToolsLastCheckedUtc"),
    routeUrl: readValue(values, "ClientMcpToolsRouteUrl", "clientMcpToolsRouteUrl")
  };
};

const resolveProviderConfigurationId = ({ wizard, values, currentConfiguration }) => getText(
  readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId"),
  readValue(values, "ResourceConfigurationId", "resourceConfigurationId"),
  getId(currentConfiguration)
);

const buildSetupOperationItem = ({ wizard, currentConfiguration, values, clientShortName }) => {
  const customFields = {
    ...(readValue(currentConfiguration, "CustomFields", "customFields") || {}),
    McpToolsClientEnabled: true,
    EnableClient: true,
    clientShortName,
    confirmationConfirmed: true,
    confirmationAction: "SetupClientMcpToolsResource",
    confirmationReason: "Enable Client MCP Tools for route validation."
  };

  return {
    ...(currentConfiguration || {}),
    ...values,
    _id: resolveProviderConfigurationId({ wizard, values, currentConfiguration }),
    ClientResourceId: readValue(currentConfiguration, "ClientResourceId", "clientResourceId") || readValue(values, "ClientResourceId", "clientResourceId") || "",
    CustomFields: customFields
  };
};

const ClientMcpToolsReadinessStep = ({
  context,
  onChange,
  onSave,
  onContinue,
  registerStepController
}) => {
  const { coreContext, coreState, managers } = useResourceDomainContext();
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceConfigurationMgr = managers?.resourceConfiguration;
  const currentConfiguration = useMemo(
    () => readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {},
    [wizard]
  );
  const storedState = getStoredReadinessState(values);
  const clientShortName = useMemo(
    () => resolveClientShortName({ coreContext, wizard, values }),
    [coreContext, values, wizard]
  );
  const route = buildToolsRoute(clientShortName);
  const routeUrl = buildAbsoluteUrl(route);
  const routeEnabled = isTruthy(readCustomField(currentConfiguration, "McpToolsRouteEnabled", "McpToolsClientEnabled"));
  const [checking, setChecking] = useState(false);
  const [localError, setLocalError] = useState("");
  const [localWarning, setLocalWarning] = useState("");
  const [statusDialog, setStatusDialog] = useState({ open: false, title: "Client MCP Tools Status", content: "" });
  const [readinessState, setReadinessState] = useState(() => storedState);
  const valuesRef = useRef(values);
  const readinessRef = useRef(readinessState);
  const busyRef = useRef(false);

  valuesRef.current = values;
  readinessRef.current = readinessState;
  busyRef.current = checking || saving;

  const updateReadinessState = useCallback((nextState) => {
    readinessRef.current = nextState || {};
    setReadinessState(nextState || {});
    onChange?.(READINESS_STATE_FIELD, nextState || {});
    onChange?.("ClientMcpToolsLastCheckStatus", nextState?.status || "");
    onChange?.("ClientMcpToolsLastCheckedUtc", nextState?.checkedAtUtc || "");
    onChange?.("ClientMcpToolsRouteUrl", routeUrl || "");
  }, [onChange, routeUrl]);

  useEffect(() => {
    const nextStoredState = getStoredReadinessState(values);
    readinessRef.current = nextStoredState || {};
    setReadinessState(nextStoredState || {});
  }, [values]);

  const ensureRouteSetup = useCallback(async (valuesToValidate = valuesRef.current) => {
    if (!resourceConfigurationMgr?.RunOperation) {
      throw new Error("Resource Configuration service is not available.");
    }
    if (!clientShortName) {
      throw new Error("Client short name is required before validating the Client MCP Tools route.");
    }

    const item = buildSetupOperationItem({
      wizard,
      currentConfiguration,
      values: valuesToValidate,
      clientShortName
    });
    const response = await resourceConfigurationMgr.RunOperation({
      operationName: Operations.ResourceConfigurationItem?.SetupClientMcpToolsResource || "SetupClientMcpToolsResource",
      item,
      queryParams: buildResourceOperationQueryParams({
        wizard,
        item,
        resourceConfiguration: currentConfiguration
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    requireSuccessfulServiceResponse(response, "Client MCP Tools route setup failed.", coreState);
    const setupConfiguration = getOperationItems(response)
      .find((configuration) => getId(configuration) === getId(item));
    const resourceConfigurationId = getId(setupConfiguration) || getId(item);
    if (!resourceConfigurationId || !resourceConfigurationMgr?.Query) {
      throw new Error("The saved Client MCP Tools configuration could not be reloaded after route setup.");
    }

    const persistedResponse = await resourceConfigurationMgr.Query({
      model: "x => x.IsDeleted == false && x._id == resourceConfigurationId",
      params: { resourceConfigurationId }
    });
    requireSuccessfulServiceResponse(
      persistedResponse,
      "The saved Client MCP Tools configuration could not be reloaded after route setup.",
      coreState
    );
    const persistedConfiguration = getOperationItems(persistedResponse)
      .find((configuration) => getId(configuration) === resourceConfigurationId);
    if (!persistedConfiguration) {
      throw new Error("The saved Client MCP Tools configuration was not returned after route setup.");
    }

    return { providerConfiguration: persistedConfiguration };
  }, [clientShortName, coreState, currentConfiguration, resourceConfigurationMgr, wizard]);

  const testCurrentUserConnection = useCallback(async ({ providerConfiguration }) => {
    const activeClientId = getText(readValue(providerConfiguration, "ClientId", "clientId"));
    if (!activeClientId) {
      throw new Error("The active client was not returned after Client MCP Tools route setup.");
    }
    const providerConfigurationId = getId(providerConfiguration);
    if (!providerConfigurationId) {
      throw new Error("The saved Client MCP Tools configuration was not returned after route setup.");
    }

    const item = {
      _id: providerConfigurationId,
      ClientId: activeClientId
    };
    const response = await resourceConfigurationMgr.RunOperation({
      operationName: TEST_CURRENT_USER_CONNECTION_OPERATION,
      id: providerConfigurationId,
      item,
      queryParams: {
        resourceConfigurationId: providerConfigurationId,
        clientId: activeClientId
      },
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    const operationMessageCode = getOperationMessageCode(response);
    try {
      requireSuccessfulServiceResponse(response, "Client MCP Tools current-user connection test failed.", coreState);
    } catch (ex) {
      ex.operationMessageCode = operationMessageCode;
      ex.isCurrentUserAuthorizationWarning = [
        PERMISSION_DENIED_EXECUTE_CODE,
        CURRENT_USER_ACCESS_WARNING_CODE
      ].includes(operationMessageCode);
      throw ex;
    }
    const snapshot = parseConnectionSnapshot(response);
    if (!snapshot) {
      throw new Error("The current-user connection test did not return a discovery result.");
    }
    return snapshot;
  }, [coreState, resourceConfigurationMgr]);

  const runRouteValidation = useCallback(async ({
    blockOnFailure = false,
    showDialog = false,
    valuesToValidate = valuesRef.current
  } = {}) => {
    setChecking(true);
    setLocalError("");
    setLocalWarning("");
    busyRef.current = true;

    try {
      const setup = await ensureRouteSetup(valuesToValidate);
      const snapshot = await testCurrentUserConnection(setup);

      const checkedAtUtc = new Date().toISOString();
      const serverInfo = readValue(snapshot, "serverInfo", "ServerInfo") || {};
      const serverName = getText(readValue(serverInfo, "name", "Name"));
      const tools = asArray(readValue(snapshot, "tools", "Tools"));
      const resources = asArray(readValue(snapshot, "resources", "Resources"));
      const checks = {
        status: {
          ok: true,
          detail: "Enabled"
        },
        initialize: {
          ok: Boolean(serverName),
          detail: serverName || "Server information was not returned"
        },
        ping: {
          ok: true,
          detail: "Authenticated"
        },
        tools: {
          ok: true,
          detail: `${tools.length} tool(s)`
        },
        resources: {
          ok: true,
          detail: `${resources.length} metadata URI(s)`
        }
      };
      const failedChecks = Object.entries(checks).filter(([, check]) => !check.ok);
      const nextState = {
        hasRun: true,
        status: failedChecks.length ? "Failed" : "Validated",
        message: failedChecks.length
          ? failedChecks.map(([name, check]) => `${name}: ${check.detail || "Failed"}`).join("; ")
          : "Client MCP Tools route validated.",
        checkedAtUtc,
        route,
        routeUrl,
        routeMode: "client",
        clientShortName,
        serverName: serverName || SERVER_NAME,
        protocolVersion: PROTOCOL_VERSION,
        authScheme: "Server-side current user",
        checks,
        responses: {
          currentUserConnection: snapshot
        }
      };
      updateReadinessState(nextState);
      setLocalWarning("");
      if (showDialog) {
        setStatusDialog({
          open: true,
          title: "Client MCP Tools Status",
          content: prettyJson(nextState.responses)
        });
      }
      if (failedChecks.length && blockOnFailure) {
        throw buildWizardValidationError(nextState.message || "Client MCP Tools route validation failed.", { suppressSummary: true });
      }
      return {
        ...(valuesToValidate || {}),
        ClientMcpToolsRouteUrl: routeUrl,
        ClientMcpToolsLastCheckStatus: nextState.status,
        ClientMcpToolsLastCheckedUtc: checkedAtUtc,
        McpToolsClientEnabled: true,
        McpToolsServerName: SERVER_NAME,
        McpToolsProtocolVersion: PROTOCOL_VERSION,
        [READINESS_STATE_FIELD]: nextState
      };
    } catch (ex) {
      const message = getErrorSummary(ex, "Client MCP Tools route validation failed.");
      const isAuthorizationWarning = ex?.isCurrentUserAuthorizationWarning === true;
      const displayMessage = isAuthorizationWarning
        ? `${message} Route setup completed, but readiness could not be verified because the current signed-in user may not have access.`
        : message;
      setLocalError(isAuthorizationWarning ? "" : displayMessage);
      setLocalWarning(isAuthorizationWarning ? displayMessage : "");
      const failedState = {
        ...(readinessRef.current || {}),
        hasRun: true,
        status: isAuthorizationWarning ? "Warnings" : "Failed",
        message: displayMessage,
        checkedAtUtc: new Date().toISOString(),
        route,
        routeUrl,
        routeMode: "client",
        clientShortName
      };
      updateReadinessState(failedState);
      if (blockOnFailure && !isAuthorizationWarning && ex?.isWizardValidationError) throw ex;
      if (blockOnFailure && !isAuthorizationWarning) {
        throw buildWizardValidationError(displayMessage, { suppressSummary: true });
      }
      return {
        ...(valuesToValidate || {}),
        ClientMcpToolsRouteUrl: routeUrl,
        ClientMcpToolsLastCheckStatus: failedState.status,
        ClientMcpToolsLastCheckedUtc: failedState.checkedAtUtc,
        [READINESS_STATE_FIELD]: failedState
      };
    } finally {
      busyRef.current = false;
      setChecking(false);
    }
  }, [clientShortName, ensureRouteSetup, route, routeUrl, testCurrentUserConnection, updateReadinessState]);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      get isBusy() {
        return Boolean(busyRef.current);
      },
      prepareSave: async (nextValues) => runRouteValidation({
        blockOnFailure: false,
        valuesToValidate: nextValues
      }),
      prepareContinue: async (nextValues) => runRouteValidation({
        blockOnFailure: true,
        showDialog: false,
        valuesToValidate: nextValues
      })
    });
    return () => registerStepController?.(null);
  }, [registerStepController, runRouteValidation]);

  const copyValue = useCallback(async (value) => {
    if (!value) return;
    try {
      await copyTextToClipboard(value);
      setLocalError("");
      setLocalWarning("");
    } catch (ex) {
      setLocalWarning("");
      setLocalError(getErrorSummary(ex, "URL could not be copied."));
    }
  }, []);

  const openValue = useCallback((value) => {
    if (!value) return;
    window.open(value, "_blank", "noopener,noreferrer");
  }, []);

  const renderUrlAbility = (label, value, copyLabel, openLabel) => value ? (
    <div className="resource-wizard__endpoint-url-ability">
      <div className="resource-wizard__endpoint-url-ability-title">
        <span>{label}</span>
        <div className="resource-wizard__endpoint-readiness-actions">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__icon-action"
            aria-label={copyLabel}
            title={copyLabel}
            onClick={() => copyValue(value)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
          </ButtonControl>
          <ButtonControl
            type="standard-small"
            className="resource-wizard__icon-action"
            aria-label={openLabel}
            title={openLabel}
            onClick={() => openValue(value)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
          </ButtonControl>
        </div>
      </div>
      <span className="resource-wizard__endpoint-url-value">{value}</span>
    </div>
  ) : null;

  const renderFactGrid = (title, facts, modifier = "") => (
    <section className={`resource-wizard__endpoint-readiness-panel${modifier ? ` resource-wizard__endpoint-readiness-panel--${modifier}` : ""}`}>
      <div className="resource-wizard__endpoint-readiness-panel-title">
        <h3>{title}</h3>
      </div>
      <div className="resource-wizard__pattern-facts resource-wizard__endpoint-readiness-facts">
        {facts.map((fact) => (
          <div className="resource-wizard__info-pair" key={fact.label}>
            <span>{fact.label}</span>
            <strong>{formatFactValue(fact.value)}</strong>
          </div>
        ))}
      </div>
    </section>
  );

  const statusText = checking ? "Validating" : readinessState?.status || "Not checked";
  const statusClass = String(statusText || "not-checked").toLowerCase().replace(/[^a-z0-9_-]/g, "-");
  const checks = readinessState?.checks || {};
  const displayedError = localError || (statusText === "Failed" ? readinessState?.message || "" : "");
  const displayedWarning = localWarning || (statusText === "Warnings" ? readinessState?.message || "" : "");
  const routeFacts = [
    { label: "Route mode", value: "client" },
    { label: "Client short name", value: clientShortName },
    { label: "HTTP route", value: `GET|POST ${route}` },
    { label: "Route enabled", value: routeEnabled ? "Enabled" : "Will enable during validation" },
    { label: "Server", value: SERVER_NAME },
    { label: "MCP protocol", value: PROTOCOL_VERSION },
    { label: "Auth", value: "Server-side current user" }
  ];
  const checkFacts = [
    { label: "Server", value: summarizeCheck(checks.status) },
    { label: "Initialize", value: summarizeCheck(checks.initialize) },
    { label: "Ping", value: summarizeCheck(checks.ping) },
    { label: "Tools list", value: summarizeCheck(checks.tools) },
    { label: "MCP metadata URIs", value: summarizeCheck(checks.resources) },
    { label: "Checked", value: formatCheckedTime(readinessState?.checkedAtUtc) }
  ];
  const hasResponseJson = Boolean(readinessState?.responses);

  return (
    <div className="resource-wizard__linked-step client-mcp-tools-readiness">
      <LinkedStepHeader
        title="Route Readiness"
        description="Validate Client MCP Tools for this client through the signed-in user's authenticated server session."
        actions={(
          <span className={`resource-wizard__readiness-status ${statusClass}`}>
            {statusText}
          </span>
        )}
      />
      {displayedWarning ? <div className="resource-wizard__inline-warning">{displayedWarning}</div> : null}
      {displayedError ? <div className="resource-wizard__inline-error">{displayedError}</div> : null}
      {checking ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
          <div className="resource-wizard__review-summary">
            {!clientShortName ? (
              <div className="resource-wizard__connection-status failed">
                Client short name is required before validating this route.
              </div>
            ) : null}
            {renderUrlAbility("MCP Server URL", routeUrl, "Copy MCP Server URL", "Open MCP Server URL")}
            {renderFactGrid("Route", routeFacts, "status")}
            {renderFactGrid("MCP Checks", checkFacts, "auth")}
            {hasResponseJson ? (
              <div className="resource-wizard__endpoint-readiness-json-action">
                <ButtonControl
                  type="standard-small"
                  className="resource-wizard__button resource-wizard__button--secondary"
                  onClick={() => setStatusDialog({ open: true, title: "Client MCP Tools Status", content: prettyJson(readinessState.responses) })}
                  disabled={checking}
                >
                  View Status JSON
                </ButtonControl>
              </div>
            ) : null}
          </div>
        </div>
      )}
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={onSave}
            disabled={readOnly || saving || checking}
            loading={checking}
          >
            Validate
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={onContinue}
            disabled={readOnly || saving || checking}
            loading={saving || checking}
          >
            Validate & Continue
          </ButtonControl>
        </div>
      </div>
      <DialogControl
        openDialog={statusDialog.open}
        title={statusDialog.title}
        okText="Close"
        cancelText=""
        onOk={() => setStatusDialog({ open: false, title: "Client MCP Tools Status", content: "" })}
        onCancel={() => setStatusDialog({ open: false, title: "Client MCP Tools Status", content: "" })}
        disableCancel={true}
      >
        <pre className="resource-wizard__endpoint-status-json">{statusDialog.content}</pre>
      </DialogControl>
    </div>
  );
};

export default ClientMcpToolsReadinessStep;
